import React from "react"
import PropTypes from "prop-types"
import { Formik, Form } from "formik"
import * as yup from "yup"
import CreatableSelect from "react-select/creatable"
import { Modal, ModalBody } from "reactstrap"
import { Bounce, toast } from "react-toastify"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import {
  ChevronDropdown,
  customStyles,
} from "../../../components/OnboardingComponents/RoleSelect"
import SettingsToggle from "../Settings/SettingsToggle"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const getInitialValues = config => ({
  domain: config?.domain || "",
  privateKey: config?.privateKey || "",
  fromAddress: config?.fromAddress || "",
  ccEmails: (config?.ccEmails || []).map(email => ({
    value: email,
    label: email,
  })),
  active: config?.status !== "inactive",
})

const validationSchema = yup.object().shape({
  domain: yup.string().trim().required("Domain is required"),
  privateKey: yup.string().trim().required("Private key is required"),
  fromAddress: yup
    .string()
    .trim()
    .email("Enter a valid from address")
    .required("From address is required"),
  ccEmails: yup.array().of(
    yup.object().shape({
      value: yup
        .string()
        .trim()
        .matches(emailPattern, "Enter valid CC email addresses"),
      label: yup.string(),
    }),
  ),
  active: yup.boolean(),
})

const ccSelectStyles = {
  ...customStyles(false),
  multiValue: base => ({
    ...base,
    background: "#f0f0f0",
    borderRadius: 8,
  }),
  multiValueLabel: base => ({
    ...base,
    fontSize: 14,
    fontWeight: 500,
    color: "#1c1c1c",
  }),
}

/**
 * Add / Edit MailGun API modal — Figma (`40005434:22365`, `40005434:22695`).
 */
const MailGunModal = ({ isOpen, onClose, onSave, config }) => {
  const isEdit = Boolean(config?.id)

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      onSave?.({
        id: config?.id,
        domain: values.domain.trim(),
        privateKey: values.privateKey.trim(),
        fromAddress: values.fromAddress.trim(),
        ccEmails: values.ccEmails.map(option => option.value.trim()).filter(Boolean),
        status: values.active ? "active" : "inactive",
      })

      toast.success(
        isEdit
          ? "MailGun configuration updated successfully."
          : "MailGun configuration saved successfully.",
        TOAST_OPTIONS,
      )
      resetForm()
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--password admin-mailgun-modal"
      contentClassName="amok-modal__content admin-mailgun-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body admin-mailgun-modal__body">
        <Formik
          key={config?.id || (isOpen ? "mailgun-open" : "mailgun-closed")}
          initialValues={getInitialValues(config)}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
          }) => (
            <Form className="admin-mailgun-modal__form">
              <div className="admin-mailgun-modal__intro">
                <h3 className="admin-mailgun-modal__title">Add MailGun API</h3>
              </div>

              <div className="admin-mailgun-modal__fields">
                <CustomInput
                  label="Domain"
                  name="domain"
                  placeholder="example.mailgun.org"
                  value={values.domain}
                  onChange={handleChange}
                  onBlur={() => setFieldTouched("domain", true)}
                  error={touched.domain && errors.domain}
                />
                <CustomInput
                  label="Private Key"
                  name="privateKey"
                  placeholder="key-xxxxxxxxxxxxxxxxxxx"
                  value={values.privateKey}
                  onChange={handleChange}
                  onBlur={() => setFieldTouched("privateKey", true)}
                  error={touched.privateKey && errors.privateKey}
                />
                <CustomInput
                  label="From Address"
                  name="fromAddress"
                  type="email"
                  placeholder="noreply@example.com"
                  value={values.fromAddress}
                  onChange={handleChange}
                  onBlur={() => setFieldTouched("fromAddress", true)}
                  error={touched.fromAddress && errors.fromAddress}
                />
                <div className="amok-field">
                  <label className="amok-field__label" htmlFor="mailgun-cc-emails">
                    CC Emails
                  </label>
                  <CreatableSelect
                    inputId="mailgun-cc-emails"
                    isMulti
                    value={values.ccEmails}
                    onChange={options =>
                      setFieldValue("ccEmails", options || [])
                    }
                    onBlur={() => setFieldTouched("ccEmails", true)}
                    placeholder="your@gmail.com , email@example.com"
                    styles={ccSelectStyles}
                    classNamePrefix="amok-role-select"
                    formatCreateLabel={inputValue => `Add "${inputValue}"`}
                    components={{
                      DropdownIndicator: ChevronDropdown,
                      IndicatorSeparator: null,
                    }}
                  />
                  {touched.ccEmails && errors.ccEmails ? (
                    <p className="amok-field__error">
                      {typeof errors.ccEmails === "string"
                        ? errors.ccEmails
                        : "Enter valid CC email addresses"}
                    </p>
                  ) : null}
                </div>

                {isEdit ? (
                  <div className="admin-mailgun-modal__status-row">
                    <label className="amok-field__label" htmlFor="mailgun-status">
                      Status
                    </label>
                    <SettingsToggle
                      checked={values.active}
                      onChange={next => setFieldValue("active", next)}
                      label="MailGun status"
                    />
                  </div>
                ) : null}
              </div>

              <div className="admin-mailgun-modal__actions">
                <CustomButton
                  variant="danger"
                  size="md"
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="primary"
                  size="md"
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {isEdit ? "Update" : "Save"}
                </CustomButton>
              </div>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  )
}

MailGunModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  config: PropTypes.shape({
    id: PropTypes.string,
    domain: PropTypes.string,
    privateKey: PropTypes.string,
    fromAddress: PropTypes.string,
    ccEmails: PropTypes.arrayOf(PropTypes.string),
    status: PropTypes.string,
  }),
}

MailGunModal.defaultProps = {
  onSave: undefined,
  config: null,
}

export default MailGunModal
